/**
 * Presentation helpers shared by the intake pages (Website inbox, Enquiry
 * queue): a one-line sender summary and a flattened key/value view of a raw
 * website form payload. Kept separate from the routing rules in intakeStreams.
 */

const str = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

/**
 * Whether a submission contains a web address.
 *
 * Nothing dangerous can arrive through these forms - the webhook takes no
 * files, and submitted text is always rendered as text, never as HTML, so a
 * link here is not clickable and cannot execute anything. The risk is only
 * that someone copies one out and opens it. Flagging it lets staff treat a
 * submission with suspicion at a glance instead of reading carefully every
 * time.
 *
 * Only explicit web addresses count. Email addresses are expected on every one
 * of these forms, so matching bare domains would flag nearly everything and
 * the warning would stop meaning anything.
 */
const WEB_ADDRESS = /(https?:\/\/|\bwww\.[a-z0-9-]+\.[a-z]{2,})/i;

export const containsLink = (payload: Record<string, unknown>): boolean =>
    Object.entries(payload ?? {})
        .filter(([k]) => !["ip", "user_agent", "referrer"].includes(k.toLowerCase()))
        .some(([, v]) => {
            if (typeof v === "string") return WEB_ADDRESS.test(v);
            if (Array.isArray(v)) return v.some((x) => typeof x === "string" && WEB_ADDRESS.test(x));
            return false;
        });

/**
 * Tooltip for a row whose reference was renumbered by migration 077, so a
 * caller quoting their original number can be matched to it. Empty when the
 * row still carries the number it was first given.
 */
export const previousRefNote = (previous: string | null | undefined): string =>
    previous ? `Previously ${previous} — quoted in any earlier email` : "";

/**
 * Maps a website feedback / complaint / compliment submission onto the
 * feedback register's form, so promoting one is a check-and-save rather than a
 * retype. Field names vary across the site's forms, so each target tries every
 * name those forms have used, and anything without a home is preserved in the
 * description rather than dropped.
 */
export const feedbackPrefillFromPayload = (
    raw: Record<string, unknown>,
    submissionType: "feedback" | "complaint" | "compliment"
): Record<string, unknown> => {
    const payload: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(raw ?? {})) {
        payload[k.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")] = v;
    }
    const pick = (...keys: string[]): string => {
        for (const key of keys) {
            const value = str(payload[key]);
            if (value) return value;
        }
        return "";
    };

    const body = pick("message", "feedback", "comments", "comment", "details",
                      "description", "your_feedback", "what_happened");
    const extras = [
        pick("subject") && `Subject: ${pick("subject")}`,
        pick("service", "service_type") && `Service: ${pick("service", "service_type")}`,
        pick("participant_name") && `Participant: ${pick("participant_name")}`,
    ].filter(Boolean).join("\n");

    return {
        submissionType,
        submissionSource: "website",
        description: [body, extras].filter(Boolean).join("\n\n"),
        submitterName: pick("full_name", "name", "contact_name", "first_name"),
        submitterEmail: pick("email", "contact_email", "email_address"),
        submitterPhone: pick("phone", "contact_phone", "mobile"),
        affectedName: pick("affected_name", "person_affected", "participant_name"),
        outcomeSought: pick("outcome_sought", "desired_outcome", "what_would_resolve_this"),
        category: pick("category", "topic", "about"),
    };
};

/** Human-friendly one-line summary per payload: best-effort name + email. */
export const senderOf = (payload: Record<string, unknown>): string => {
    const keys = Object.keys(payload ?? {});
    const grab = (...names: string[]) => {
        for (const n of names) {
            const k = keys.find((key) => key.toLowerCase().replace(/[^a-z0-9]+/g, "_") === n);
            if (k && str(payload[k])) return str(payload[k]);
        }
        return "";
    };
    const name = grab("full_name", "name", "contact_name", "referrer_name", "first_name");
    const email = grab("email", "contact_email", "email_address");
    return [name, email].filter(Boolean).join(" · ") || "Unknown sender";
};

/** The payload minus plumbing keys, flattened for display. */
export const detailsOf = (payload: Record<string, unknown>): Array<[string, string]> =>
    Object.entries(payload ?? {})
        .filter(([k]) => !["form", "form_name", "ip", "user_agent", "referrer"].includes(k.toLowerCase()))
        .map(([k, v]): [string, string] => [
            k.replace(/[_-]+/g, " "),
            Array.isArray(v) ? v.map(str).filter(Boolean).join(", ")
                : typeof v === "object" && v !== null ? JSON.stringify(v)
                : String(v ?? ""),
        ])
        .filter(([, v]) => v.length > 0);
